import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Loader, Plus, X, RotateCcw, Save, FileText, History, AlertTriangle, Check, Pause, Play } from 'lucide-react';
import { api, API_BASE_URL } from '../services/api';
import { businessToday } from '../utils/time';
import PlanBidButton from '../components/PlanBidButton';
import AccountMark from '../components/AccountMark';

/**
 * The Plan Cost Sheet, in the app.
 *
 * The owner, Sep 17 2026: "allow me to edit the treatments as easy as editing
 * this sheet in the app, also run the ops side of things with this sheet."
 *
 * Laid out like KAM-Plan-Cost-Sheet.xlsx: yellow cells are typed, everything
 * else is worked out — live, by the same engine the rest of the app prices and
 * books from (server/services/planSheet). Nothing is saved until Save.
 */

type Sheet = any;
const money = (n: any, d = 2) => (n == null || n === '' || !Number.isFinite(Number(n)) ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })}`);
const pct = (n: any) => (n == null || !Number.isFinite(Number(n)) ? '—' : `${(Number(n) * 100).toFixed(1)}%`);
const PLAN_KEYS = ['full', 'plan', 'simple'] as const;   // highest price first, the way the owner presents tiers
const PLAN_NAMES: Record<string, string> = { simple: 'Simple', plan: 'The Plan', full: 'Estate' };
const GRASS_NAMES: Record<string, string> = { bz: 'Bermuda / Zoysia', sa: 'St. Augustine' };
const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

/** A yellow input cell, the way the xlsx marks what you type. */
function Cell({ value, onChange, type = 'number', width = 'w-20', step, align = 'right' }: {
  value: any; onChange: (v: any) => void; type?: string; width?: string; step?: string; align?: 'left' | 'right';
}) {
  return (
    <input
      type={type} step={step} value={value ?? ''}
      onChange={e => onChange(type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
      className={`${width} bg-yellow-50 border border-yellow-300 rounded px-1.5 py-1 text-xs text-${align} focus:outline-none focus:ring-2 focus:ring-yellow-400`}
    />
  );
}

export default function PlanSheet() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'inputs' | 'plans' | 'scale' | 'month' | 'enroll' | 'requests'>(
    () => (new URLSearchParams(window.location.search).get('tab') === 'requests' ? 'requests' : 'inputs'));
  const { data: requestData } = useQuery('plan-requests', () => api.get('/plans/requests').then(r => r.data.requests as any[]), { refetchInterval: 120000 });
  const { data, isLoading } = useQuery('plan-sheet', () => api.get('/plan-sheet').then(r => r.data), { refetchOnWindowFocus: false });

  const [draft, setDraft] = useState<Sheet | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data?.sheet && !dirty) { setDraft(data.sheet); setSummary(data.summary); setErrors([]); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Live recalculation: the server engine works the edit out, nothing is saved.
  const timer = useRef<any>(null);
  function edit(mutator: (s: Sheet) => void) {
    setDraft((prev: Sheet) => {
      const next = JSON.parse(JSON.stringify(prev));
      mutator(next);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        api.post('/plan-sheet/preview', { sheet: next }).then(r => { setSummary(r.data.summary); setErrors(r.data.errors || []); }).catch(() => {});
      }, 350);
      return next;
    });
    setDirty(true);
  }

  const save = useMutation(() => api.put('/plan-sheet', { sheet: draft }).then(r => r.data), {
    onSuccess: (d: any) => {
      setDirty(false); setSummary(d.summary); setDraft(d.sheet); setErrors([]);
      qc.invalidateQueries('plan-sheet'); qc.invalidateQueries('plan-sheet-history');
      toast.success(d.message || 'Saved');
    },
    onError: (e: any) => { const d = e?.response?.data; setErrors(d?.errors || []); toast.error(d?.message || 'Could not save'); },
  });
  const reset = useMutation(() => api.post('/plan-sheet/reset').then(r => r.data), {
    onSuccess: (d: any) => { setDirty(false); setDraft(d.sheet); setSummary(d.summary); qc.invalidateQueries('plan-sheet'); toast.success(d.message); },
    onError: () => { toast.error('Could not reset'); },
  });
  const [showHistory, setShowHistory] = useState(false);
  const [flyerSqft, setFlyerSqft] = useState('');

  if (isLoading || !draft || !summary) {
    return <div className="py-20 flex justify-center"><Loader className="h-6 w-6 animate-spin text-brand-600" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs text-gray-500"><Link to="/treatments" className="hover:underline">Treatments</Link> / Plan Cost Sheet</p>
          <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Plan Cost Sheet</h1>
          <p className="text-sm text-gray-500">Yellow cells are yours to type. Prices, the calendar, the crew's amounts, the buy list and the flyer all come from here.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {dirty && <span className="text-xs font-semibold text-amber-700">Unsaved changes</span>}
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
            <span className="whitespace-nowrap">Flyer sq ft</span>
            <input type="number" min={500} max={80000} step={100} inputMode="numeric"
              value={flyerSqft}
              placeholder={String(draft.settings?.default_sqft || 6000)}
              onChange={e => setFlyerSqft(e.target.value)}
              className="w-24 bg-yellow-50 border border-yellow-300 rounded px-1.5 py-1 text-xs text-right focus:outline-none focus:ring-2 focus:ring-yellow-400" />
          </label>
          <a href={`${API_BASE_URL}/plan-sheet/flyer?sqft=${encodeURIComponent(String(Number(flyerSqft) >= 500 ? Math.round(Number(flyerSqft)) : (draft.settings?.default_sqft || 6000)))}`}
            target="_blank" rel="noreferrer" className="btn-secondary text-xs inline-flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> Flyer</a>
          <button onClick={() => setShowHistory(v => !v)} className="btn-secondary text-xs inline-flex items-center gap-1"><History className="h-3.5 w-3.5" /> History</button>
          {dirty && <button onClick={() => { setDirty(false); setDraft(data.sheet); setSummary(data.summary); setErrors([]); }} className="btn-secondary text-xs">Discard</button>}
          <button onClick={() => save.mutate()} disabled={!dirty || save.isLoading || errors.length > 0}
            className="btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-50"><Save className="h-3.5 w-3.5" /> {save.isLoading ? 'Saving…' : 'Save'}</button>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          <p className="font-semibold flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> The sheet won't save like this</p>
          <ul className="list-disc ml-5 mt-1 text-xs">{errors.slice(0, 8).map(e => <li key={e}>{e}</li>)}</ul>
        </div>
      )}

      {showHistory && <HistoryPanel onRestored={(d: any) => { setDirty(false); setDraft(d.sheet); setSummary(d.summary); qc.invalidateQueries('plan-sheet'); }}
        onReset={() => { if (window.confirm('Put the sheet back exactly as the xlsx came in? Your current copy is kept in History.')) reset.mutate(); }} />}

      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {([['inputs', 'Inputs'], ['plans', 'Plans & calendar'], ['scale', 'Scale'], ['month', 'This month'], ['enroll', 'Put a yard on a plan'],
           ['requests', `Requests${(requestData || []).length ? ` (${(requestData || []).length})` : ''}`]] as const).map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === k ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>{label}</button>
        ))}
      </div>

      {tab === 'inputs' && <InputsTab draft={draft} summary={summary} edit={edit} services={data?.meta?.services || {}} />}
      {tab === 'plans' && <PlansTab draft={draft} summary={summary} edit={edit} services={data?.meta?.services || {}} />}
      {tab === 'scale' && <ScaleTab draft={draft} summary={summary} />}
      {tab === 'month' && <MonthTab dirty={dirty} />}
      {tab === 'enroll' && <EnrollTab />}
      {tab === 'requests' && <RequestsTab requests={requestData || []} />}
    </div>
  );
}

// ── Inputs: settings + products ──────────────────────────────────────────────
function InputsTab({ draft, summary, edit, services }: { draft: Sheet; summary: any; edit: (m: (s: Sheet) => void) => void; services: any }) {
  const s = draft.settings;
  const byKey: Record<string, any> = Object.fromEntries((summary.products || []).map((p: any) => [p.key, p]));
  return (
    <div className="space-y-4">
      <div className="card p-4">
        <h2 className="text-xs font-black uppercase tracking-wider text-gray-600 mb-3">Settings</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm">
          <label className="flex items-center justify-between gap-2">Labor rate ($/hr) <Cell value={s.labor_rate} onChange={v => edit(x => { x.settings.labor_rate = v; })} /></label>
          <label className="flex items-center justify-between gap-2">Default lawn (sq ft) <Cell value={s.default_sqft} onChange={v => edit(x => { x.settings.default_sqft = v; })} /></label>
          <label className="flex items-center justify-between gap-2">Target margin <Cell value={s.target_margin} step="0.01" onChange={v => edit(x => { x.settings.target_margin = v; })} /></label>
          <label className="flex items-center justify-between gap-2">Waste on product <Cell value={s.waste_pct} step="0.01" onChange={v => edit(x => { x.settings.waste_pct = v; })} /></label>
          <label className="flex items-center justify-between gap-2">Not mowing with us (×) <Cell value={s.nonmow_multiplier} step="0.1" onChange={v => edit(x => { x.settings.nonmow_multiplier = v; })} /></label>
        </div>
        <h3 className="text-xs font-black uppercase tracking-wider text-gray-600 mt-4 mb-2">The card — $ a month at {Number(s.default_sqft).toLocaleString('en-US')} sq ft, already mowing</h3>
        <div className="flex flex-wrap gap-4 text-sm">
          {PLAN_KEYS.map(k => (
            <label key={k} className="flex items-center gap-2">{PLAN_NAMES[k]} <Cell value={s.card_monthly?.[k]} onChange={v => edit(x => { x.settings.card_monthly[k] = v; })} /></label>
          ))}
        </div>
        <p className="text-[11px] text-gray-500 mt-2">Every other lawn size scales from these by the sheet's own cost, and never drops below the sheet at target margin. One price for both grasses.</p>
      </div>

      <div className="card overflow-x-auto">
        <div className="px-4 pt-4 flex items-center justify-between">
          <h2 className="text-xs font-black uppercase tracking-wider text-gray-600">Products — type the SiteOne price</h2>
          <button onClick={() => edit(x => { x.products.push({ key: `NEW${x.products.length}`, name: 'New product', pack: 1, unit: 'lb', price: 0, rate: 1, rate_unit: 'lb', labor_min: 0, service: 'fertilizer', grasses: ['bz', 'sa'], note: '' }); })}
            className="text-xs font-semibold text-brand-700 inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add a product</button>
        </div>
        <table className="w-full text-xs mt-2">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="px-2 py-2 text-left">Key</th><th className="px-2 py-2 text-left">Product</th>
              <th className="px-2 py-2 text-right">Pack</th><th className="px-2 py-2 text-left">Unit</th>
              <th className="px-2 py-2 text-right">Your price</th><th className="px-2 py-2 text-right">Rate / 1,000</th>
              <th className="px-2 py-2 text-left">Rate unit</th><th className="px-2 py-2 text-right">Labor min</th>
              <th className="px-2 py-2 text-left">Customer word</th><th className="px-2 py-2 text-left">Grass</th>
              <th className="px-2 py-2 text-right">Cost / 1,000</th><th className="px-2 py-2 text-right">Product / app</th><th className="px-2 py-2 text-right">Labor / app</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {draft.products.map((p: any, i: number) => {
              const c = byKey[p.key] || {};
              const set = (k: string) => (v: any) => edit(x => { x.products[i][k] = v; });
              return (
                <tr key={i} className="border-t border-gray-100 align-top">
                  <td className="px-2 py-1.5 font-mono font-semibold text-gray-700">{p.key}</td>
                  <td className="px-2 py-1.5"><Cell type="text" width="w-48" align="left" value={p.name} onChange={set('name')} />
                    {p.note ? <p className="text-[10px] text-gray-500 mt-0.5 max-w-[12rem]">{p.note}</p> : null}</td>
                  <td className="px-2 py-1.5 text-right"><Cell width="w-16" value={p.pack} onChange={set('pack')} /></td>
                  <td className="px-2 py-1.5"><Cell type="text" width="w-14" align="left" value={p.unit} onChange={set('unit')} /></td>
                  <td className="px-2 py-1.5 text-right"><Cell width="w-20" value={p.price} onChange={set('price')} /></td>
                  <td className="px-2 py-1.5 text-right"><Cell width="w-16" step="0.001" value={p.rate} onChange={set('rate')} /></td>
                  <td className="px-2 py-1.5"><Cell type="text" width="w-14" align="left" value={p.rate_unit} onChange={set('rate_unit')} /></td>
                  <td className="px-2 py-1.5 text-right"><Cell width="w-14" value={p.labor_min} onChange={set('labor_min')} /></td>
                  <td className="px-2 py-1.5">
                    <select value={p.service} onChange={e => set('service')(e.target.value)} className="bg-yellow-50 border border-yellow-300 rounded px-1 py-1 text-xs">
                      {Object.entries(services).map(([k, v]: any) => <option key={k} value={k}>{v.word}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {(['bz', 'sa'] as const).map(g => (
                      <label key={g} className="mr-2 inline-flex items-center gap-1">
                        <input type="checkbox" checked={(p.grasses || []).includes(g)}
                          onChange={e => edit(x => { const gs = new Set(x.products[i].grasses || []); if (e.target.checked) gs.add(g); else gs.delete(g); x.products[i].grasses = Array.from(gs); })} />
                        {g === 'bz' ? 'B/Z' : 'SA'}
                      </label>
                    ))}
                  </td>
                  <td className="px-2 py-1.5 text-right text-gray-700">{money(c.cost_per_1k)}</td>
                  <td className="px-2 py-1.5 text-right text-gray-700">{money(c.product_per_app)}</td>
                  <td className="px-2 py-1.5 text-right text-gray-700">{money(c.labor_per_app)}</td>
                  <td className="px-2 py-1.5">
                    <button title="Remove this product" onClick={() => {
                      if (!window.confirm(`Remove ${p.name}? It comes out of every plan month it is in.`)) return;
                      edit(x => {
                        const key = x.products[i].key;
                        x.products.splice(i, 1);
                        for (const g of Object.keys(x.calendar)) for (const pl of Object.keys(x.calendar[g])) x.calendar[g][pl] = x.calendar[g][pl].map((m: string[]) => m.filter(k => k !== key));
                      });
                    }} className="text-gray-300 hover:text-red-600"><X className="h-4 w-4" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="px-4 py-3 text-[11px] text-gray-500">Product / app is at the default lawn with waste. Units "visit" and "each" cost their price flat, no waste. The customer word is all a customer ever sees — never the product name.</p>
      </div>
    </div>
  );
}

// ── Plans: the month calendar, the counts, the money ─────────────────────────
function PlansTab({ draft, summary, edit, services }: { draft: Sheet; summary: any; edit: (m: (s: Sheet) => void) => void; services: any }) {
  const [grass, setGrass] = useState<'bz' | 'sa'>('bz');
  const products: any[] = draft.products;
  const plans: any[] = summary.plans?.[grass] || [];
  const card: Record<string, any> = Object.fromEntries((summary.card || []).map((c: any) => [c.plan, c]));
  const allowed = products.filter(p => (p.grasses || []).includes(grass));
  const nameOf = (k: string) => products.find(p => p.key === k)?.name || k;
  const wordOf = (k: string) => services[products.find(p => p.key === k)?.service]?.word || '';

  return (
    <div className="space-y-4">
      <div className="flex rounded-lg border border-gray-300 overflow-hidden w-fit text-sm">
        {(['bz', 'sa'] as const).map(g => (
          <button key={g} onClick={() => setGrass(g)} className={`press px-4 py-1.5 font-medium ${grass === g ? 'bg-brand-600 text-black' : 'bg-white text-gray-700'}`}>{GRASS_NAMES[g]}</button>
        ))}
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-800 text-white">
            <tr><th className="px-2 py-2 text-left w-24">Month</th>{PLAN_KEYS.map(k => <th key={k} className="px-2 py-2 text-left">{PLAN_NAMES[k]}</th>)}</tr>
          </thead>
          <tbody>
            {MONTHS.map((mon, mi) => (
              <tr key={mon} className="border-t border-gray-100 align-top">
                <td className="px-2 py-2">
                  <p className="font-bold text-gray-800">{mon}</p>
                  <p className="text-[10px] text-gray-500">{summary.years?.[grass]?.[mi]?.note}</p>
                </td>
                {PLAN_KEYS.map(k => {
                  const keys: string[] = draft.calendar?.[grass]?.[k]?.[mi] || [];
                  return (
                    <td key={k} className="px-2 py-2">
                      <p className="text-[11px] text-gray-500 mb-1">{summary.years?.[grass]?.[mi]?.[k]}</p>
                      <div className="flex flex-wrap gap-1">
                        {keys.map((pk, idx) => (
                          <span key={`${pk}-${idx}`} title={`${nameOf(pk)} — ${wordOf(pk)}`}
                            className="inline-flex items-center gap-0.5 rounded bg-yellow-50 border border-yellow-300 px-1.5 py-0.5 font-mono text-[10px] text-gray-800">
                            {pk}
                            <button onClick={() => edit(x => { x.calendar[grass][k][mi].splice(idx, 1); })} className="text-gray-500 hover:text-red-600"><X className="h-3 w-3" /></button>
                          </span>
                        ))}
                        <select value="" onChange={e => { const v = e.target.value; if (v) edit(x => { x.calendar[grass][k][mi].push(v); }); }}
                          className="text-[10px] border border-dashed border-gray-300 rounded px-1 py-0.5 text-gray-500 bg-white">
                          <option value="">+ add</option>
                          {allowed.map(p => <option key={p.key} value={p.key}>{p.key} · {services[p.service]?.word}</option>)}
                        </select>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card overflow-x-auto">
        <h2 className="px-4 pt-4 text-xs font-black uppercase tracking-wider text-gray-600">App counts / year — {GRASS_NAMES[grass]}</h2>
        <table className="w-full text-xs mt-2">
          <thead className="bg-gray-50 text-gray-500">
            <tr><th className="px-3 py-2 text-left">Product</th>{PLAN_KEYS.map(k => <th key={k} className="px-3 py-2 text-right">{PLAN_NAMES[k]}</th>)}</tr>
          </thead>
          <tbody>
            {products.filter(p => (p.grasses || []).includes(grass)).map(p => (
              <tr key={p.key} className="border-t border-gray-100">
                <td className="px-3 py-1.5"><span className="font-mono text-gray-500">{p.key}</span> {p.name} <span className="text-gray-500">· {services[p.service]?.word}</span></td>
                {plans.map((pl: any) => <td key={pl.plan} className="px-3 py-1.5 text-right">{pl.counts?.[p.key] || 0}</td>)}
              </tr>
            ))}
            <Row label="Apps / year" values={plans.map(p => p.apps)} bold />
            <Row label="Annual COGS" values={plans.map(p => money(p.cogs))} bold />
            <Row label={`Sell at ${pct(draft.settings.target_margin)} margin`} values={plans.map(p => money(p.sell))} />
            <Row label="Monthly at margin" values={plans.map(p => money(p.monthly))} />
            <Row label="COGS / app (avg)" values={plans.map(p => money(p.avg_per_app))} />
            <Row label="Card $/mo (mowing)" values={PLAN_KEYS.map(k => money(card[k]?.month, 0))} bold />
            <Row label="Card $/year" values={PLAN_KEYS.map(k => money(card[k]?.year, 0))} />
            <Row label="Margin at card price" values={plans.map(p => (card[p.plan]?.year ? pct((card[p.plan].year - p.cogs) / card[p.plan].year) : '—'))} bold />
          </tbody>
        </table>
        <div className="px-4 py-3 flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-gray-500">
          {plans.map((p: any) => (
            <span key={p.plan}><b>{PLAN_NAMES[p.plan]}</b>: {Object.entries(p.services || {}).map(([k, n]: any) => `${n} ${services[k]?.word || k}`).join(', ') || 'nothing'}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function Row({ label, values, bold = false }: { label: string; values: any[]; bold?: boolean }) {
  return (
    <tr className={`border-t border-gray-200 ${bold ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
      <td className="px-3 py-1.5">{label}</td>
      {values.map((v, i) => <td key={i} className="px-3 py-1.5 text-right">{v}</td>)}
    </tr>
  );
}

// ── Scale: any lawn size ─────────────────────────────────────────────────────
function ScaleTab({ draft, summary }: { draft: Sheet; summary: any }) {
  const [sqft, setSqft] = useState('');
  const { data: one } = useQuery(['plan-price', sqft], () => api.get('/plan-sheet/price', { params: { sqft } }).then(r => r.data),
    { enabled: Number(sqft) >= 500, keepPreviousData: true });
  return (
    <div className="space-y-4">
      <div className="card p-4 flex flex-wrap items-end gap-3">
        <label className="text-sm text-gray-700">Price a yard<input type="number" value={sqft} onChange={e => setSqft(e.target.value)} placeholder="sq ft of turf" className="input mt-1 w-40" /></label>
        {one?.plans?.length > 0 && (
          <div className="flex flex-wrap gap-4 text-sm">
            {one.plans.map((p: any) => (
              <span key={p.plan}><b>{p.name}</b> ${p.month}/mo <span className="text-gray-500 line-through">${p.nonmow_month}</span> · ${p.year.toLocaleString('en-US')}/yr</span>
            ))}
          </div>
        )}
        <p className="text-[11px] text-gray-500 w-full">Uses the saved sheet. The table below follows your unsaved edits.</p>
      </div>
      <div className="card overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="px-2 py-2 text-left">Sq ft</th>
              {PLAN_KEYS.map(k => <th key={k} colSpan={4} className="px-2 py-2 text-center border-l border-gray-200">{PLAN_NAMES[k]}</th>)}
            </tr>
            <tr>
              <th />
              {PLAN_KEYS.map(k => (
                <React.Fragment key={k}>
                  <th className="px-2 py-1 text-right border-l border-gray-200">COGS B/Z</th><th className="px-2 py-1 text-right">COGS SA</th>
                  <th className="px-2 py-1 text-right">$/mo</th><th className="px-2 py-1 text-right">Not mowing</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {(summary.scale || []).map((row: any) => (
              <tr key={row.sqft} className={`border-t border-gray-100 ${row.sqft === draft.settings.default_sqft ? 'bg-yellow-50 font-semibold' : ''}`}>
                <td className="px-2 py-1.5">{row.sqft.toLocaleString('en-US')}</td>
                {row.plans.map((p: any) => (
                  <React.Fragment key={p.plan}>
                    <td className="px-2 py-1.5 text-right border-l border-gray-100">{money(p.cogs_bz, 0)}</td>
                    <td className="px-2 py-1.5 text-right">{money(p.cogs_sa, 0)}</td>
                    <td className="px-2 py-1.5 text-right text-gray-900">${p.month}</td>
                    <td className="px-2 py-1.5 text-right text-gray-500">${p.nonmow_month}</td>
                  </React.Fragment>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="px-4 py-3 text-[11px] text-gray-500">Product grows with the yard (plus waste). Labor does not. The highlighted row is the card.</p>
      </div>
    </div>
  );
}

// ── This month: the ops side ─────────────────────────────────────────────────
function MonthTab({ dirty }: { dirty: boolean }) {
  const qc = useQueryClient();
  // Austin's month, not UTC's: after 7 PM Central on the last day the UTC month is already the next one.
  const [month, setMonth] = useState(() => businessToday().slice(0, 7));
  const { data, isLoading } = useQuery(['plan-month', month], () => api.get('/plan-sheet/month', { params: { month } }).then(r => r.data));
  const rebuild = useMutation((dry: boolean) => api.post('/plan-sheet/rebuild', { dry_run: dry }).then(r => r.data), {
    onSuccess: (d: any) => {
      if (d.dry_run) {
        if (d.programs && window.confirm(`${d.message}\n\nGo ahead?`)) rebuild.mutate(false);
        else if (!d.programs) toast.info('No yards are on a plan yet.');
        return;
      }
      toast.success(d.message); qc.invalidateQueries('plan-month');
    },
    onError: () => { toast.error('Could not apply the sheet'); },
  });
  const pause = useMutation(({ id, on }: { id: number; on: boolean }) => api.post(`/plan-sheet/programs/${id}/${on ? 'resume' : 'pause'}`).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d.message); qc.invalidateQueries('plan-month'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not change that plan'); },
  });

  const t = data?.totals || {};
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm text-gray-700">Month<input type="month" value={month} onChange={e => setMonth(e.target.value)} className="input mt-1" /></label>
        <button onClick={() => rebuild.mutate(true)} disabled={dirty || rebuild.isLoading} className="btn-secondary text-xs" title={dirty ? 'Save the sheet first' : ''}>
          Apply the sheet to booked visits
        </button>
        {dirty && <span className="text-[11px] text-amber-700">Save the sheet first — booked visits follow the saved copy.</span>}
      </div>

      {isLoading || !data ? <p className="text-sm text-gray-500">Loading…</p> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Visits" value={`${t.done}/${t.visits}`} />
            <Stat label="Plans billing" value={money(t.revenue, 0)} />
            <Stat label="Product + labor" value={money(t.cost, 0)} />
            <Stat label="Margin" value={pct(t.margin)} />
            <Stat label="To buy" value={money(t.buy_cost, 0)} />
          </div>
          {t.sheet_changed > 0 && (
            <p className="text-xs rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2">
              {t.sheet_changed} visit{t.sheet_changed === 1 ? ' was' : 's were'} booked before the sheet changed. Apply the sheet to bring them up to date.
            </p>
          )}

          <div className="card overflow-x-auto">
            <h2 className="px-4 pt-4 text-xs font-black uppercase tracking-wider text-gray-600">Visits — {data.month_name}</h2>
            {data.visits.length === 0 ? <p className="px-4 py-3 text-sm text-gray-500">No plan visits this month.</p> : (
              <table className="w-full text-xs mt-2">
                <thead className="bg-gray-50 text-gray-500"><tr>
                  <th className="px-3 py-2 text-left">Day</th><th className="px-3 py-2 text-left">Yard</th><th className="px-3 py-2 text-left">Plan</th>
                  <th className="px-3 py-2 text-left">What goes down</th><th className="px-3 py-2 text-right">Cost</th><th className="px-3 py-2 text-left">Status</th>
                </tr></thead>
                <tbody>
                  {data.visits.map((v: any) => (
                    <tr key={v.round_id} className="border-t border-gray-100 align-top">
                      <td className="px-3 py-2 whitespace-nowrap">{v.job_id ? <Link to={`/jobs/${v.job_id}`} className="text-brand-700 hover:underline">{v.date}</Link> : v.date}</td>
                      <td className="px-3 py-2"><p className="font-medium text-gray-900">{v.customer}</p><p className="text-gray-500">{v.address}{v.route ? ` · ${v.route}` : ''}{v.rides_a_mow ? ' · on the mow' : ''}</p></td>
                      <td className="px-3 py-2 whitespace-nowrap">{PLAN_NAMES[v.plan]} · {v.grass === 'bz' ? 'B/Z' : 'SA'} · {Number(v.sqft).toLocaleString('en-US')} sq ft</td>
                      <td className="px-3 py-2">
                        <p className="text-gray-900">{v.words}</p>
                        <p className="text-gray-500">{v.lines.map((l: any) => `${l.key} ${l.unit === 'visit' || l.unit === 'each' ? '×1' : `${l.amount} ${l.unit}`}`).join(' · ')}</p>
                        {v.sheet_changed && v.status !== 'complete' && <p className="text-amber-700">Booked before the sheet changed</p>}
                      </td>
                      <td className="px-3 py-2 text-right">{money(v.cost, 0)}</td>
                      <td className="px-3 py-2">{v.status === 'complete' ? <span className="text-emerald-700 inline-flex items-center gap-1"><Check className="h-3 w-3" /> done</span> : v.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="card overflow-x-auto">
            <h2 className="px-4 pt-4 text-xs font-black uppercase tracking-wider text-gray-600">What to buy — visits not done yet, with waste</h2>
            {data.buy.length === 0 ? <p className="px-4 py-3 text-sm text-gray-500">Nothing to buy.</p> : (
              <table className="w-full text-xs mt-2">
                <thead className="bg-gray-50 text-gray-500"><tr>
                  <th className="px-3 py-2 text-left">Product</th><th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2 text-right">Packs</th><th className="px-3 py-2 text-right">Cost</th>
                </tr></thead>
                <tbody>
                  {data.buy.map((b: any) => (
                    <tr key={b.key} className="border-t border-gray-100">
                      <td className="px-3 py-1.5">{b.name} <span className="text-gray-500">· {b.word}</span></td>
                      <td className="px-3 py-1.5 text-right">{b.amount} {b.unit}</td>
                      <td className="px-3 py-1.5 text-right">{b.unit === 'visit' || b.unit === 'each' ? '—' : `${b.packs} × ${b.pack}`}</td>
                      <td className="px-3 py-1.5 text-right">{money(b.cost, 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="card overflow-x-auto">
            <h2 className="px-4 pt-4 text-xs font-black uppercase tracking-wider text-gray-600">Yards on a plan</h2>
            {data.programs.length === 0 ? <p className="px-4 py-3 text-sm text-gray-500">Nobody yet — use "Put a yard on a plan".</p> : (
              <table className="w-full text-xs mt-2">
                <tbody>
                  {data.programs.map((p: any) => (
                    <tr key={p.id} className="border-t border-gray-100">
                      <td className="px-3 py-2"><p className="font-medium text-gray-900">{p.customer_name}</p><p className="text-gray-500">{p.address}</p></td>
                      <td className="px-3 py-2">{p.name}</td>
                      <td className="px-3 py-2 text-right">{Number(p.sqft).toLocaleString('en-US')} sq ft</td>
                      <td className="px-3 py-2 text-right">${Number(p.monthly_price).toFixed(0)}/mo{p.mow ? '' : ' (not mowed)'}</td>
                      <td className="px-3 py-2 text-right">since {p.start}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => pause.mutate({ id: p.id, on: p.status === 'paused' })} disabled={pause.isLoading}
                          className="text-xs font-semibold text-brand-700 inline-flex items-center gap-1">
                          {p.status === 'paused' ? <><Play className="h-3 w-3" /> Resume</> : <><Pause className="h-3 w-3" /> Pause</>}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card p-3">
      <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-lg font-bold text-gray-900">{value}</p>
    </div>
  );
}

// ── Put a yard on a plan ─────────────────────────────────────────────────────
function EnrollTab() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [customer, setCustomer] = useState<any>(null);
  const [propertyId, setPropertyId] = useState<number | null>(null);
  const [form, setForm] = useState<any>({ plan: 'plan', grass: '', sqft: '', mow: null, start: '' });
  const [preview, setPreview] = useState<any>(null);

  const { data: clients } = useQuery('plan-clients', () => api.get('/quotes/pickers/customers').then(r => (Array.isArray(r.data) ? r.data : [])), { staleTime: 300000 });
  const needle = search.trim().toLowerCase();
  const matches = useMemo(() => (needle.length > 1 ? (clients || []).filter((c: any) => String(c.name).toLowerCase().includes(needle)).slice(0, 6) : []), [clients, needle]);
  const { data: props } = useQuery(['plan-props', customer?.id], () => api.get('/quotes/pickers/properties', { params: { customer_id: customer.id } }).then(r => r.data.properties), { enabled: !!customer });
  useEffect(() => { if (props?.length && !propertyId) setPropertyId(props[0].id); }, [props, propertyId]);
  const { data: yardData } = useQuery(['plan-yard', propertyId], () => api.get(`/plan-sheet/yard/${propertyId}`).then(r => r.data), { enabled: !!propertyId });
  useEffect(() => {
    if (yardData?.yard) {
      setForm((f: any) => ({ ...f, grass: yardData.yard.grass || '', sqft: yardData.yard.sqft ? String(yardData.yard.sqft) : '', mow: yardData.yard.mowed, start: yardData.start }));
      setPreview(null);
    }
  }, [yardData]);

  const body = () => ({ property_id: propertyId, customer_id: customer?.id, plan: form.plan, grass: form.grass || undefined, sqft: form.sqft || undefined, mow: form.mow, start: form.start || undefined });
  const look = useMutation(() => api.post('/plan-sheet/enroll', { ...body(), dry_run: true }).then(r => r.data), {
    onSuccess: (d: any) => setPreview(d.preview),
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not price that'); },
  });
  const book = useMutation(() => api.post('/plan-sheet/enroll', body()).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d.message); setPreview(null); setCustomer(null); setPropertyId(null); setSearch(''); qc.invalidateQueries('plan-month'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not book that'); },
  });

  return (
    <div className="card p-4 space-y-3 max-w-2xl">
      <p className="text-sm text-gray-600">Send the three plans as a bid they sign, or book one now if they already said yes. Each month's visit rides that month's mow, with the crew's amounts on the job.</p>
      {!customer ? (
        <>
          <input className="input" placeholder="Search a customer" value={search} onChange={e => setSearch(e.target.value)} autoFocus />
          {matches.map((c: any) => (
            <button key={c.id} onClick={() => { setCustomer(c); setPropertyId(null); }} className="block w-full text-left px-3 py-2 rounded-lg border border-gray-200 text-sm hover:border-brand-400">{c.name}<AccountMark mark={c.account_mark} /></button>
          ))}
        </>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="font-semibold text-gray-900">{customer.name}</p>
            <button onClick={() => { setCustomer(null); setPropertyId(null); setPreview(null); }} className="text-xs text-gray-500">Change</button>
          </div>
          {(props || []).length > 1 && (
            <select className="input" value={propertyId || ''} onChange={e => { setPropertyId(Number(e.target.value)); setPreview(null); }}>
              {(props || []).map((p: any) => <option key={p.id} value={p.id}>{[p.label, p.address].filter(Boolean).join(' — ')}</option>)}
            </select>
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-gray-600">Plan
              <select className="input mt-1" value={form.plan} onChange={e => { setForm({ ...form, plan: e.target.value }); setPreview(null); }}>
                {PLAN_KEYS.map(k => <option key={k} value={k}>{PLAN_NAMES[k]}</option>)}
              </select>
            </label>
            <label className="text-xs text-gray-600">Grass {yardData?.yard?.grass_type ? <span className="text-gray-500">(on file: {yardData.yard.grass_type})</span> : null}
              <select className="input mt-1" value={form.grass} onChange={e => { setForm({ ...form, grass: e.target.value }); setPreview(null); }}>
                <option value="">Pick one</option>
                <option value="bz">Bermuda / Zoysia</option>
                <option value="sa">St. Augustine</option>
              </select>
            </label>
            <label className="text-xs text-gray-600">Turf sq ft {yardData?.yard?.sqft_source ? <span className="text-gray-500">({yardData.yard.sqft_source})</span> : null}
              <input className="input mt-1" type="number" value={form.sqft} onChange={e => { setForm({ ...form, sqft: e.target.value }); setPreview(null); }} placeholder="type it" />
            </label>
            <label className="text-xs text-gray-600">Start month
              <input className="input mt-1" type="month" value={form.start} onChange={e => { setForm({ ...form, start: e.target.value }); setPreview(null); }} />
            </label>
            <label className="text-xs text-gray-600 col-span-2 flex items-center gap-2">
              <input type="checkbox" checked={!!form.mow} onChange={e => { setForm({ ...form, mow: e.target.checked }); setPreview(null); }} />
              We mow this yard (the big price). Unticked is the crossed-out price.
            </label>
          </div>
          {propertyId && <PlanBidButton propertyId={propertyId} customerId={customer.id} phone={customer.phone} customerName={customer.name} />}
          <p className="text-[11px] text-gray-500">Or start them now if they already said yes — nothing is texted:</p>
          <button onClick={() => look.mutate()} disabled={look.isLoading || !propertyId} className="btn-secondary text-sm">{look.isLoading ? 'Working it out…' : 'Show the price and the visits'}</button>
          {preview && (
            <div className="rounded-lg border border-gray-200 p-3 space-y-2">
              <p className="text-sm text-gray-900"><b>{preview.plan_name}</b> · {preview.grass_name} · {Number(preview.sqft).toLocaleString('en-US')} sq ft — <b>${preview.monthly}/mo</b> (${preview.yearly.toLocaleString('en-US')} a year){preview.mow ? '' : ', not mowed by us'}</p>
              <ul className="text-xs text-gray-600 space-y-0.5">
                {preview.visits.map((v: any) => <li key={v.month}>{v.date} — {v.words}{v.rides ? ' · rides the mow' : ''}</li>)}
              </ul>
              <button onClick={() => book.mutate()} disabled={book.isLoading} className="btn-primary text-sm">{book.isLoading ? 'Booking…' : `Book it — ${preview.visits.length} visits`}</button>
              <p className="text-[11px] text-gray-500">Billed ${preview.monthly} on each month-end bill from {preview.start}. Nothing is texted or charged from here.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * What customers asked for from their own page, a public link or a bid.
 *
 * The owner's rule (Sep 17 2026): it comes to him. Approving books the plan
 * from the sheet with their own mix, or puts the extra on their next mow.
 * Nothing is texted or charged either way.
 */
function RequestsTab({ requests }: { requests: any[] }) {
  const qc = useQueryClient();
  const act = useMutation(({ id, how }: { id: number; how: 'approve' | 'decline' }) => api.post(`/plans/requests/${id}/${how}`).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d.message || 'Done'); qc.invalidateQueries('plan-requests'); qc.invalidateQueries('plan-month'); qc.invalidateQueries('attention'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not do that'); },
  });

  if (!requests.length) {
    return <div className="card p-6 text-sm text-gray-500">Nothing waiting. What customers ask for from their page, a bid or the public plans link lands here.</div>;
  }
  return (
    <div className="space-y-3">
      {requests.map(r => (
        <div key={r.id} className="card p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="font-semibold text-gray-900">{r.title}</p>
              <p className="text-sm text-gray-600">{r.detail}</p>
              <p className="text-xs text-gray-500 mt-1">
                {r.source === 'portal' ? 'From their own page' : r.source === 'quote' ? 'From a bid' : 'From the public plans link'}
                {r.phone ? ` · ${r.phone}` : ''}{r.email ? ` · ${r.email}` : ''}
                {!r.customer_id ? ' · not a customer yet' : ''}
              </p>
              {r.items && (
                <p className="text-xs text-gray-500 mt-1">
                  {Object.entries(r.items).map(([k, n]: any) => `${n} ${k}`).join(' · ')}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => act.mutate({ id: r.id, how: 'decline' })} disabled={act.isLoading}
                className="btn-secondary text-xs">Not now</button>
              <button onClick={() => act.mutate({ id: r.id, how: 'approve' })} disabled={act.isLoading}
                className="btn-primary text-xs">Approve and book</button>
            </div>
          </div>
          {!r.customer_id && (
            <p className="mt-2 text-[11px] text-amber-700">
              Make the customer and the yard first (Customers → New), then approve. The price stays what they were shown.
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

function HistoryPanel({ onRestored, onReset }: { onRestored: (d: any) => void; onReset: () => void }) {
  const { data } = useQuery('plan-sheet-history', () => api.get('/plan-sheet/history').then(r => r.data.history));
  const restore = useMutation((id: number) => api.post(`/plan-sheet/history/${id}/restore`).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d.message); onRestored(d); },
    onError: () => { toast.error('Could not put that back'); },
  });
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-black uppercase tracking-wider text-gray-600">Earlier copies</h2>
        <button onClick={onReset} className="text-xs text-gray-600 inline-flex items-center gap-1"><RotateCcw className="h-3.5 w-3.5" /> Back to the xlsx as it came in</button>
      </div>
      {(data || []).length === 0 ? <p className="text-sm text-gray-500">No saves yet — the sheet is the xlsx as it came in.</p> : (
        <ul className="divide-y divide-gray-100 text-sm">
          {(data || []).map((h: any) => (
            <li key={h.id} className="py-1.5 flex items-center justify-between gap-2">
              <span className="text-gray-700">{String(h.saved_at).slice(0, 16).replace('T', ' ')} · {h.note}{h.saved_by ? ` · ${h.saved_by}` : ''}</span>
              <button onClick={() => { if (window.confirm('Put this copy back? The current one is kept too.')) restore.mutate(h.id); }} className="text-xs font-semibold text-brand-700">Put back</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
